export const LEAVE_STATUSES = [
  { value: '', label: 'Todos los estados' },
  { value: 'draft', label: 'Borrador' },
  { value: 'pending', label: 'Pendiente' },
  { value: 'observed', label: 'Observada' },
  { value: 'approved', label: 'Aprobada' },
  { value: 'rejected', label: 'Rechazada' },
  { value: 'cancelled', label: 'Cancelada' },
  { value: 'suspended', label: 'Suspendida' },
];

export const LEAVE_CATEGORIES = [
  { value: '', label: 'Todas' },
  { value: 'license', label: 'Licencia' },
  { value: 'justification', label: 'Justificación' },
  { value: 'franchise', label: 'Franquicia' },
  { value: 'permission', label: 'Permiso' },
  { value: 'administrative_rule', label: 'Regla administrativa' },
];

export const LEAVE_MANAGER_ROLES = ['admin', 'executive', 'director', 'administrative'];

export const LEAVE_SELF_ROLES = ['teacher', 'janitor', 'rti', 'student_assistant'];

export const isLeaveManagerRole = (role) => LEAVE_MANAGER_ROLES.includes(role);

export const isLeaveSelfRole = (role) => LEAVE_SELF_ROLES.includes(role);

export const canViewLeaveDashboard = (role) => isLeaveManagerRole(role) || isLeaveSelfRole(role);

export const statusLabel = (status) => {
  const item = LEAVE_STATUSES.find((entry) => entry.value === status);
  return item ? item.label : status || '-';
};

export const statusClass = (status) => {
  const classes = {
    draft: 'bg-gray-100 text-gray-700 border-gray-200',
    pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    observed: 'bg-orange-100 text-orange-800 border-orange-200',
    approved: 'bg-green-100 text-green-800 border-green-200',
    rejected: 'bg-red-100 text-red-800 border-red-200',
    cancelled: 'bg-gray-100 text-gray-500 border-gray-200',
    suspended: 'bg-blue-100 text-blue-800 border-blue-200',
  };
  return classes[status] || 'bg-gray-100 text-gray-700 border-gray-200';
};

export const categoryLabel = (category) => {
  const item = LEAVE_CATEGORIES.find((entry) => entry.value === category);
  return item ? item.label : category || '-';
};

const LEAVE_REQUEST_RELATIONS = [
  'documents',
  'leave_request_documents',
  'attachments',
  'requirements',
  'leave_request_requirements',
  'days',
  'history',
  'events',
  'status_history',
];

export const unwrapData = (response) => {
  const payload = response?.data ?? response;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (payload?.data && typeof payload.data === 'object') {
    // Leave request detail puts relations as siblings of `data`
    // ({ data, documents, requirements, days }), not nested inside it.
    const relations = {};
    for (const key of LEAVE_REQUEST_RELATIONS) {
      if (payload[key] !== undefined && payload.data[key] === undefined) {
        relations[key] = payload[key];
      }
    }
    if (Object.keys(relations).length) {
      return { ...payload.data, ...relations };
    }
    return payload.data;
  }
  return payload;
};

export const unwrapList = (response) => {
  const value = unwrapData(response);
  return Array.isArray(value) ? value : [];
};

export const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(`${value}`.includes('T') ? value : `${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
};

export const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

export const formatMonthYear = (value) => {
  const date = value instanceof Date ? value : new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('es-AR', {
    month: 'long',
    year: 'numeric',
  }).format(date);
};

export const todayISO = () => new Date().toISOString().slice(0, 10);

export const daysBetweenInclusive = (startDate, endDate) => {
  if (!startDate || !endDate) return 0;
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return 0;
  return Math.round((end - start) / 86400000) + 1;
};

export const articleTitle = (article = {}) => {
  const code = article.code || article.article_code || article.leave_article_code;
  const name = article.name || article.article_name || article.leave_article_name;
  return [code, name].filter(Boolean).join(' — ') || '-';
};

export const requestTitle = (request = {}) => {
  const code = request.article_code || request.leave_article_code || request.article?.code;
  const name = request.article_name || request.leave_article_name || request.article?.name;
  return [code, name].filter(Boolean).join(' — ') || request.title || 'Solicitud de licencia';
};

export const teacherProfile = (teacher = {}) => teacher.Profile || teacher.profile || teacher.user?.profile || {};

const pickFirstName = (...sources) => {
  for (const source of sources) {
    if (!source || typeof source !== 'object') continue;
    const value = source.firstname || source.first_name || source.nombre;
    if (value) return value;
  }
  return '';
};

const pickLastName = (...sources) => {
  for (const source of sources) {
    if (!source || typeof source !== 'object') continue;
    const value = source.lastname || source.last_name || source.apellido;
    if (value) return value;
  }
  return '';
};

const formatPersonName = (first, last) => {
  if (last && first) return `${last}, ${first}`;
  return [last, first].filter(Boolean).join(' ').trim();
};

export const teacherFullName = (teacher = {}) => {
  const profile = teacherProfile(teacher);
  const first = pickFirstName(teacher, profile);
  const last = pickLastName(teacher, profile);
  return formatPersonName(first, last) || teacher.name || teacher.full_name || teacher.email || 'Docente';
};

export const requestTeacherName = (request = {}) => {
  const user = request.user || request.teacher || request.employee || {};
  const profile = user.Profile || user.profile || request.user_profile || request.profile || request.Profile || {};
  const direct = request.user_name || request.teacher_name || request.employee_name;
  if (direct) return direct;
  const first = pickFirstName(request, profile, user);
  const last = pickLastName(request, profile, user);
  return formatPersonName(first, last) || request.user_email || 'Docente';
};

export const requestTeacherMeta = (request = {}) => {
  const profile = request.user_profile || request.profile || request.user?.profile || request.user?.Profile || {};
  const fileNumber = request.file_number || request.legajo || profile.file_number || profile.legajo;
  const dni = request.dni || profile.dni;
  return [fileNumber ? `Legajo ${fileNumber}` : '', dni ? `DNI ${dni}` : ''].filter(Boolean).join(' · ');
};

export const requestDocuments = (request = {}) =>
  request.documents || request.leave_request_documents || request.attachments || [];

export const requestRequirements = (request = {}) =>
  request.requirements || request.leave_request_requirements || [];

export const requestHistory = (request = {}) =>
  request.history || request.events || request.status_history || [];

export const requirementStatusLabel = (status, passed) => {
  if (passed && !status) return 'Cumplido';
  const labels = {
    pending: 'Pendiente',
    passed: 'Cumplido',
    failed: 'Incumplido',
    not_applicable: 'No aplica',
  };
  return labels[status] || status || 'Pendiente';
};

export const requirementStatusClass = (status, passed) => {
  if (passed && !status) return 'bg-green-50 text-green-700 border-green-100';
  const classes = {
    pending: 'bg-yellow-50 text-yellow-800 border-yellow-100',
    passed: 'bg-green-50 text-green-700 border-green-100',
    failed: 'bg-red-50 text-red-700 border-red-100',
    not_applicable: 'bg-gray-50 text-gray-700 border-gray-200',
  };
  return classes[status] || classes.pending;
};

export const documentStatusLabel = (status) => {
  const labels = {
    pending: 'Pendiente',
    approved: 'Aprobado',
    rejected: 'Rechazado',
  };
  return labels[status] || status || 'Pendiente';
};

export const documentStatusClass = (status) => {
  const classes = {
    pending: 'bg-yellow-50 text-yellow-800 border-yellow-100',
    approved: 'bg-green-50 text-green-700 border-green-100',
    rejected: 'bg-red-50 text-red-700 border-red-100',
  };
  return classes[status] || classes.pending;
};

export const canEditRequest = (request = {}) => ['draft', 'observed'].includes(request.status);

export const canCancelRequest = (request = {}) => ['draft', 'pending', 'observed'].includes(request.status);

export const canUploadDocument = (request = {}) =>
  ['draft', 'pending', 'observed', 'suspended'].includes(request.status);
